import { MapDrawerEntityForPoiMetadataByType } from 'shared/types';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import { PrivateLandOwnershipContent } from '../layers/PrivateLandOwnershipLayer/PrivateLandOwnershipContent';
import { ExternalFeatureDrawerContent } from '../layers/ExternalFeatureLayer/ExternalFeatureDrawerContent';

type ContentComponents = {
  [K in keyof MapDrawerEntityForPoiMetadataByType]: (
    props: MapDrawerEntityForPoiMetadataByType[K],
  ) => JSX.Element;
};

/**
 * Maps drawer POI types to content components.
 *
 * - Built-in types: 'privateLandOwnership', 'mapPin'
 * - External feature layers: Handled by fallback component
 *
 * Types must be unique per layer as selection matches by both layer type and feature ID.
 * The Proxy pattern provides explicit mappings for known types while defaulting others
 * to ExternalFeatureDrawerContent.
 */
const MAP_DRAWER_ENTITY_CONTENT_COMPONENTS: ContentComponents = new Proxy(
  {
    privateLandOwnership: PrivateLandOwnershipContent,
  } as ContentComponents,
  {
    get: (target, prop) => {
      return prop in target
        ? target[prop as string]
        : ExternalFeatureDrawerContent;
    },
  },
);

export const MapDrawerEntityContentForPoi = (): JSX.Element | null => {
  const { mapDrawerEntityForPoi } = useMapDrawerEntityForPoiState();

  if (!mapDrawerEntityForPoi) {
    return null;
  }

  const { type, metadata } = mapDrawerEntityForPoi;

  // POIs with no content/dialog but still needing a focused state
  // will have metadata set to null.
  if (!metadata) {
    return null;
  }

  const Content = MAP_DRAWER_ENTITY_CONTENT_COMPONENTS[type];

  return <Content {...metadata} key={type} />;
};
